import { X, CheckCircle, Clock, ListTodo } from 'lucide-react';

type PlanItem = {
  id: string;
  messageId: string;
  sessionId?: string | null;
  messageContent: string;
  priority?: 'low' | 'medium' | 'high';
  status?: 'todo' | 'in_progress' | 'done';
  timeframe?: string;
  dueDate?: string | null;
  successMetric?: string | null;
  coachType?: string;
  createdAt?: string;
};

type Props = {
  isOpen: boolean;
  items: PlanItem[];
  isLoading?: boolean;
  isConfirming?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onGenerateMoM: () => void;
};

export default function PlanSummarySheet({ isOpen, items, isLoading, isConfirming, onClose, onConfirm, onGenerateMoM }: Props) {
  if (!isOpen) return null;

  const getPriorityBadge = (p?: string) => {
    switch ((p || 'medium').toLowerCase()) {
      case 'high':
        return 'bg-red-50 text-red-700 border-red-100';
      case 'low':
        return 'bg-blue-50 text-blue-700 border-blue-100';
      default:
        return 'bg-amber-50 text-amber-700 border-amber-100';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-t-2xl bg-white p-6 shadow-2xl ring-1 ring-black/5 sm:rounded-xl sm:my-16">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#4C62E3]/10 text-[#4C62E3]"><ListTodo className="h-5 w-5"/></div>
            <h2 className="font-display text-xl font-bold text-slate-900">Review Your Plan</h2>
          </div>
          <button onClick={onClose} className="rounded-full bg-slate-100 p-2 text-slate-500 hover:bg-slate-200" aria-label="Close plan">
            <X size={18} />
          </button>
        </div>

        <p className="mb-4 text-sm text-slate-600">These are the focus areas you saved in this session. Confirm to start execution, or generate a summary.</p>

        <div className="max-h-[50vh] overflow-y-auto space-y-3 pr-1">
          {isLoading ? (
            <div className="text-sm text-slate-500">Loading...</div>
          ) : items.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">No focus areas saved yet.</div>
          ) : (
            items.map((item) => (
              <div key={item.id} className="rounded-xl border border-slate-200 p-4 bg-white">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-display text-sm font-semibold text-slate-900">{item.messageContent}</h3>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border ${getPriorityBadge(item.priority)}`}>
                        {(item.priority || 'medium')} Priority
                      </span>
                      {item.timeframe && (
                        <span className="flex items-center gap-1 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                          <Clock className="h-3 w-3" /> {item.timeframe}
                        </span>
                      )}
                      {item.successMetric && (
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Metric: {item.successMetric}</span>
                      )}
                    </div>
                  </div>
                  {item.status === 'done' ? (
                    <CheckCircle className="h-5 w-5 text-emerald-500" />
                  ) : null}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button onClick={onGenerateMoM} className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            Generate MoM Now
          </button>
          <button
            onClick={onConfirm}
            disabled={isConfirming || items.length === 0}
            className="rounded-lg bg-[#4C62E3] px-4 py-2 text-sm font-semibold text-white hover:bg-[#3B4CC5] disabled:opacity-60"
          >
            {isConfirming ? 'Confirming…' : 'Confirm Plan'}
          </button>
        </div>
      </div>
    </div>
  );
}
